import React, { useEffect, useState, useCallback } from 'react';
import { Search, UserPlus, Loader2, X, Check, Users as UsersIcon, Download } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import client from '../lib/api';
import { AdminError } from './components/PageState';
import { formatDateTime, formatMoney } from './lib/formatters';
import PageSizeSelect from './components/PageSizeSelect';
import { readPageSize, writePageSize } from './lib/pagination';
import { UserActionsMenu, useUserActions } from './components/UserActions';

// İşlem penceresi ve menü ortak dosyalardadır: üye profili de aynı menüyü
// çizer (bkz. components/UserActions.jsx). Testler pencereyi buradan alır.
export { UserModal, twoFactorChannelLabel } from './components/UserModal';

// Aranacak alanı yönetici seçer; tek kutuda bütün alanları taramak "deniz"
// araması için ad, soyad, kullanıcı adı ve e-postada geçen onlarca kaydı
// birden getiriyordu. Anahtarlar backend'in `field` süzgeciyle aynıdır
// (admin_router.USER_SEARCH_FIELDS); ayrışırsa uç 422 döner.
const SEARCH_FIELDS = [
  { id: 'username', label: 'Kullanıcı adı', placeholder: 'Kullanıcı adı yazın…' },
  { id: 'first_name', label: 'Ad', placeholder: 'Yalnızca adı yazın…' },
  { id: 'last_name', label: 'Soyad', placeholder: 'Yalnızca soyadı yazın…' },
  { id: 'name', label: 'Ad Soyad', placeholder: 'Ad ve soyadı birlikte yazın…' },
  { id: 'tc_no', label: 'TC No', placeholder: 'TC kimlik numarası yazın…' },
  { id: 'email', label: 'E-posta', placeholder: 'E-posta adresi yazın…' },
  { id: 'phone', label: 'Telefon No', placeholder: 'Telefon numarası yazın…' },
  // Üye no = kayıttaki kalıcı üye kimliği (UUID). Listedeki "#1" sütunu
  // sayfa sırasıdır, sayfa değişince kayar; onunla arama yapılamaz.
  { id: 'id', label: 'Üye No', placeholder: 'Üye kimliğini (ID) yazın…' },
  { id: 'all', label: 'Tüm alanlar', placeholder: 'Tüm alanlarda ara…' },
];

const STATUS_TABS = [
  { id: 'all', label: 'Tümü' },
  { id: 'active', label: 'Aktif' },
  { id: 'passive', label: 'Pasif' },
  { id: 'banned', label: 'Yasaklı', className: 'tab-red' },
  { id: 'unverified', label: 'Doğrulama Doğrulanmadı', className: 'tab-red' },
  { id: 'verified', label: 'Doğrulama Doğrulandı', className: 'tab-green' },
];

// Dışa aktarımda seçilebilen alanlar. Anahtarlar backend'in kümesiyle
// aynıdır (admin_router.USER_EXPORT_COLUMNS); ayrışırsa uç 400 döner.
const EXPORT_COLUMNS = [
  { id: 'username', label: 'Kullanıcı adı' },
  { id: 'name', label: 'Ad Soyad', hint: 'Ad ve soyad ayrı sütunlara yazılır' },
  { id: 'tc_no', label: 'TC kimlik no' },
  { id: 'birth_date', label: 'Doğum tarihi' },
  { id: 'phone', label: 'Telefon no' },
  { id: 'email', label: 'E-posta' },
];

// Hangi üyelerin dosyaya gireceği. Ölçü listedeki YATIRIM / ÇEKİM
// sütunlarıdır (onaylı toplamlar); anahtarlar backend'in `activity`
// süzgeciyle aynıdır.
const EXPORT_ACTIVITY = [
  { id: 'all', label: 'Tüm üyeler' },
  { id: 'deposit', label: 'Yalnızca yatırım yapanlar' },
  { id: 'withdraw', label: 'Yalnızca çekim yapanlar' },
  { id: 'both', label: 'Hem yatırım hem çekim yapanlar' },
];

// Dosya blob olarak istendiği için hata gövdesi de blob gelir; okunmazsa
// yönetici "100.000 satır sınırı" gibi sebebi göremez.
const readBlobError = async (error) => {
  const body = error?.response?.data;
  if (!body) return '';
  try {
    const text = typeof body === 'string' ? body
      : typeof body.text === 'function' ? await body.text() : '';
    return JSON.parse(text)?.detail || '';
  } catch {
    return '';
  }
};

const ExportDialog = ({ search, status, total, onClose }) => {
  const [selected, setSelected] = useState(['username']);
  const [activity, setActivity] = useState('all');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const allSelected = selected.length === EXPORT_COLUMNS.length;

  const toggle = (id) => setSelected((current) => (
    current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
  ));

  const download = async () => {
    setBusy(true);
    setError('');
    try {
      // Süzgeç ekrandaki listeyle aynıdır; sayfalama gönderilmez, uyan
      // üyelerin tamamı yazılır.
      const response = await client.get('/admin/users/export', {
        params: {
          columns: selected.join(','),
          q: search.q || undefined,
          field: search.q ? search.field : undefined,
          exact: search.q && search.exact ? true : undefined,
          status,
          activity: activity === 'all' ? undefined : activity,
        },
        responseType: 'blob',
      });
      const stamp = new Date().toISOString().slice(0, 10);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `kullanicilar-${stamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      onClose();
    } catch (requestError) {
      setError(await readBlobError(requestError) || 'Dosya indirilemedi.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={() => !busy && onClose()}>
      <div className="absolute inset-0 bg-black/80 backdrop-blur-md" />
      <div
        className="relative admin-card admin-modal w-full max-w-md"
        role="dialog"
        aria-modal="true"
        aria-labelledby="user-export-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="admin-modal-head">
          <div className="admin-modal-icon"><Download size={19} /></div>
          <div className="min-w-0">
            <h2 id="user-export-title" className="font-black text-[15px] leading-tight">Kullanıcıları Dışa Aktar</h2>
            <p className="text-[11px] text-slate-500 mt-0.5">Excel dosyasına yazılacak bilgileri seçin</p>
          </div>
          <button type="button" onClick={onClose} disabled={busy} className="icon-btn view ml-auto" aria-label="Kapat"><X size={14} /></button>
        </div>

        <div className="admin-modal-body">
          <label className="block mb-4">
            <span className="text-xs text-slate-400 block mb-1.5">Üye kapsamı</span>
            <select
              value={activity}
              onChange={(event) => setActivity(event.target.value)}
              className="admin-input"
            >
              {EXPORT_ACTIVITY.map((item) => (
                <option key={item.id} value={item.id}>{item.label}</option>
              ))}
            </select>
          </label>
          <label className="flex cursor-pointer items-center gap-2 border-b border-white/5 pb-3 mb-3 text-sm font-semibold">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={() => setSelected(allSelected ? [] : EXPORT_COLUMNS.map((item) => item.id))}
            />
            Tümünü seç
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {EXPORT_COLUMNS.map((item) => (
              <label key={item.id} className="flex cursor-pointer items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={selected.includes(item.id)}
                  onChange={() => toggle(item.id)}
                />
                <span>
                  {item.label}
                  {item.hint && <span className="block text-[11px] text-slate-500">{item.hint}</span>}
                </span>
              </label>
            ))}
          </div>
          <p className="mt-4 text-xs text-slate-400">
            {activity === 'all' ? (
              <>Ekrandaki arama ve sekme süzgecine uyan <b className="text-slate-200">{total}</b> kullanıcının
              tamamı aktarılır.</>
            ) : (
              <>Ekrandaki arama ve sekme süzgecine uyan {total} kullanıcıdan yalnızca
              {' '}<b className="text-slate-200">{EXPORT_ACTIVITY.find((item) => item.id === activity).label.toLowerCase()}</b> aktarılır.</>
            )}
            {' '}İndirme işlem geçmişine kaydedilir.
          </p>
          {error && <p className="mt-3 text-xs text-red-400" role="alert">{error}</p>}
        </div>

        <div className="admin-modal-foot">
          <span className="admin-modal-summary">{selected.length} alan seçili</span>
          <div className="ml-auto flex items-center gap-2">
            <button type="button" onClick={onClose} disabled={busy} className="admin-btn-secondary">Vazgeç</button>
            <button type="button" onClick={download} disabled={busy || selected.length === 0} className="admin-btn-primary flex items-center gap-2">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Excel İndir
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Badge = ({ ok, label }) => (
  <span className={`badge-pill ${ok ? 'badge-green' : 'badge-red'}`}>
    {ok ? <Check size={11} /> : <X size={11} />} {label}
  </span>
);


const Users = () => {
  const navigate = useNavigate();
  // Arama adresten de başlatılabilir (`?ara=kullanici&tam=1`): adres her zaman
  // kullanıcı adı alanında arar. İlk değerler buradan okunur; aksi hâlde sayfa
  // önce bütün listeyi çeker, sonra aramaya geçerdi.
  const [params, setParams] = useSearchParams();
  const urlTerm = (params.get('ara') || '').trim();
  const urlExact = params.get('tam') === '1';
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  // `term`/`field` kutudaki taslak, `search` uygulanmış aramadır: liste her
  // harfte değil yalnızca Ara'ya basınca yenilenir.
  const [term, setTerm] = useState(urlTerm);
  const [field, setField] = useState('username');
  // Tam eşleşme kutusu: "furkan" araması furkan123 / furkanaga kayıtlarını
  // getirmesin. Alan seçimiyle birlikte çalışır, onun yerine geçmez.
  const [exact, setExact] = useState(urlExact);
  const [search, setSearch] = useState({ q: urlTerm, field: 'username', exact: urlExact });
  const [status, setStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [skip, setSkip] = useState(0);
  // Sayfa boyutu yöneticinin tercihidir ve tarayıcısında saklanır.
  const [pageSize, setPageSize] = useState(() => readPageSize('users'));
  const [exportOpen, setExportOpen] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await client.get('/admin/users', {
        params: {
          q: search.q || undefined,
          field: search.q ? search.field : undefined,
          exact: search.q && search.exact ? true : undefined,
          status,
          skip,
          limit: pageSize,
        },
      });
      setUsers(data.items || []); setTotal(data.total || 0);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Kullanıcı listesi alınamadı');
    } finally { setLoading(false); }
  }, [search, status, skip, pageSize]);

  useEffect(() => { load(); }, [load]);

  // Sayfa açıkken adres değişirse (aynı rotaya verilen bağlantı) bileşen
  // yeniden kurulmaz; arama burada uygulanır. Sonuç gizli kalmasın diye durum
  // sekmesi de Tümü'ne döner. Aynı aramada `search` nesnesi korunur, yoksa
  // ilk açılışta liste iki kez çekilirdi.
  useEffect(() => {
    if (!urlTerm) return;
    setTerm(urlTerm);
    setField('username');
    setExact(urlExact);
    setStatus('all');
    setSkip(0);
    setSearch((current) => (
      current.q === urlTerm && current.field === 'username' && current.exact === urlExact
        ? current
        : { q: urlTerm, field: 'username', exact: urlExact }
    ));
  }, [urlTerm, urlExact]);

  const { openModal, menuHandlers, dialogs } = useUserActions({ reload: load });

  const runSearch = (event) => {
    event?.preventDefault();
    setSkip(0);
    setSearch({ q: term.trim(), field, exact });
  };

  const clearSearch = () => {
    setTerm('');
    setSkip(0);
    setSearch({ q: '', field, exact });
    // Adres temizlenmezse sayfa yenilenince silinen arama geri gelirdi.
    if (params.has('ara')) setParams({}, { replace: true });
  };

  // Alan değiştirildiğinde uygulanmış arama varsa liste yeni alana göre
  // tazelenir; eski alanın sonucunu ekranda bırakmak "arama çalışmıyor" gibi
  // görünüyordu. Tam eşleşme kutusu da aynı sebeple listeyi hemen tazeler:
  // kutuyu işaretleyip Ara'ya basmayı beklemek "kutu çalışmıyor" gibi
  // görünüyordu.
  const changeField = (next) => {
    setField(next);
    if (search.q) {
      setSkip(0);
      setSearch({ q: search.q, field: next, exact: search.exact });
    }
  };

  const changeExact = (next) => {
    setExact(next);
    if (search.q) {
      setSkip(0);
      setSearch({ q: search.q, field: search.field, exact: next });
    }
  };

  const activeField = SEARCH_FIELDS.find((item) => item.id === field)
    || SEARCH_FIELDS[0];

  return (
    <div>
      {/* Search + Add */}
      <div className="flex flex-col md:flex-row items-stretch md:items-start gap-3 mb-4">
        <form onSubmit={runSearch} className="flex-1">
          <div className="flex flex-col sm:flex-row items-stretch gap-2">
            <select
              value={field}
              onChange={(e) => changeField(e.target.value)}
              className="admin-input admin-input-w-md"
              aria-label="Aranacak alan"
            >
              {SEARCH_FIELDS.map((item) => (
                <option key={item.id} value={item.id}>{item.label}</option>
              ))}
            </select>
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-cyan-400 z-10" />
              <input
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                placeholder={activeField.placeholder}
                className={`admin-input pl-10 ${term ? 'pr-10' : ''}`}
                aria-label={`${activeField.label} ara`}
              />
              {term && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white z-10"
                  aria-label="Aramayı temizle"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            <button type="submit" className="admin-btn-primary h-11 px-5 flex items-center justify-center gap-2">
              <Search size={16} /> Ara
            </button>
          </div>
          {/* Kutu arama alanının altındadır: seçili alanla birlikte çalışır,
              onun yerine geçmez. */}
          <label className="mt-2 flex w-fit cursor-pointer items-center gap-2 text-xs text-slate-400 hover:text-slate-200">
            <input
              type="checkbox"
              checked={exact}
              onChange={(event) => changeExact(event.target.checked)}
            />
            <span>
              Tam eşleşme —{' '}
              <b className="text-slate-300">{activeField.label.toLowerCase()}</b>
              {' '}birebir aynı olan kayıtlar gelsin
            </span>
          </label>
        </form>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => setExportOpen(true)} className="admin-btn-secondary h-11 px-5 flex items-center gap-2"><Download size={16} /> Dışa Aktar</button>
          <button onClick={() => openModal({ type: 'create' })} className="admin-btn-primary h-11 px-5 flex items-center gap-2"><UserPlus size={16} /> Yeni Kullanıcı Ekle</button>
        </div>
      </div>

      {search.q && (
        <div className="mb-3 text-xs text-slate-400">
          <b className="text-slate-200">{activeField.label}</b> alanında
          {' '}<b className="text-slate-200">“{search.q}”</b>
          {search.exact ? ' tam eşleşme' : ''} araması —
          {' '}{total} sonuç
          <button type="button" onClick={clearSearch} className="ml-2 text-cyan-400 hover:underline">
            aramayı temizle
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap mb-4">
        {STATUS_TABS.map((t) => (
          <button key={t.id} onClick={() => { setSkip(0); setStatus(t.id); }} className={`admin-tab ${t.className || ''} ${status === t.id ? 'active' : ''}`}>
            {t.id === 'unverified' && '⚠ '}{t.id === 'verified' && '✓ '}{t.label}
            {status === t.id && total > 0 && ` (${total})`}
          </button>
        ))}
      </div>

      {/* Users Table */}
      <div className="admin-card overflow-hidden">
        <div className="px-5 py-3 flex items-center gap-2 border-b border-white/5">
          <UsersIcon size={16} className="text-cyan-400" />
          <h3 className="font-bold">Kullanıcı Listesi</h3>
          <span className="ml-auto text-xs text-slate-400">{total} kullanıcı</span>
        </div>
        <div className="overflow-x-auto">
          <table className="admin-table w-full min-w-[1000px]">
            <thead>
              <tr>
                <th>ID</th>
                <th>KULLANICI</th>
                <th>KYC</th>
                <th>AFFILIATE</th>
                <th>BAKİYE</th>
                <th>KALAN ÇEVRİM</th>
                <th>YATIRIM</th>
                <th>ÇEKİM</th>
                <th>KÂR/ZARAR</th>
                <th>DURUM</th>
                <th>KAYIT</th>
                <th>İŞLEMLER</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={12} className="text-center py-10"><Loader2 className="inline animate-spin text-cyan-400 mr-2" /> Yükleniyor...</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={12} className="text-center py-10 text-slate-500">Kullanıcı bulunamadı</td></tr>
              ) : users.map((u) => (
                <tr key={u.id}>
                  {/* Sıra numarası sayfaya göre kayar; "Üye No" ile aranan
                      kalıcı kimlik ipucunda durur. */}
                  <td className="text-slate-400" title={`Üye No: ${u.id}`}>#{u.seq || '-'}</td>
                  <td>
                    {/* shrink-0: uzun kullanıcı adı baş harf kutusunu ezip
                        dikdörtgene çevirmesin diye. */}
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 shrink-0 rounded-lg flex items-center justify-center text-xs font-black leading-none" style={{ background: 'linear-gradient(180deg, #38bdf8, #0284c7)', color: '#06131f' }}>{(u.username || 'US').slice(0, 2).toUpperCase()}</div>
                      <div className="min-w-0">
                        <div className="font-semibold">{u.username}{u.is_admin && <span className="badge-pill badge-yellow ml-2">Admin</span>}</div>
                        <div className="text-xs text-slate-500">{(u.first_name || '') + ' ' + (u.last_name || '')}</div>
                      </div>
                    </div>
                  </td>
                  <td><Badge ok={u.kyc_verified} label={u.kyc_verified ? 'Doğrulandı' : 'Bekliyor'} /></td>
                  <td><Badge ok={u.can_use_affiliate} label={u.can_use_affiliate ? 'Yetkili' : 'Yetkisiz'} /></td>
                  <td className="admin-money font-bold">{formatMoney(u.balance)}</td>
                  <td className={`admin-money font-bold ${Number(u.wager_remaining || 0) > 0 ? 'text-amber-400' : 'text-green-400'}`}>
                    {formatMoney(u.wager_remaining)}
                  </td>
                  <td className="admin-money text-green-400">{formatMoney(u.total_deposit)}</td>
                  <td className="admin-money text-red-400">{formatMoney(u.total_withdraw)}</td>
                  <td className={`admin-money ${u.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>{formatMoney(u.profit)}</td>
                  <td>
                    <div className="flex flex-wrap items-center gap-1">
                      {u.is_banned ? <span className="badge-pill badge-red">Yasaklı</span>
                      : u.is_active ? <span className="badge-pill badge-green"><span className="w-1.5 h-1.5 rounded-full bg-green-400" /> Aktif</span>
                      : <span className="badge-pill badge-gray">Pasif</span>}
                      {/* Kısıt hesabı kapatmadığı için durum rozeti tek başına
                          göstermez; ayrı işaretlenmezse yönetici üyenin neden
                          talep açamadığını göremez. */}
                      {u.deposit_blocked && <span className="badge-pill badge-red">Yatırım kısıtlı</span>}
                      {u.withdraw_blocked && <span className="badge-pill badge-red">Çekim kısıtlı</span>}
                      {u.bonus_blocked && <span className="badge-pill badge-red">Bonus yasaklı</span>}
                    </div>
                  </td>
                  {/* Son giriş (IP + konum) listede gösterilmez; kullanıcı
                      detay sayfasında zaten var. */}
                  <td className="text-xs text-slate-400">{formatDateTime(u.created_at)}</td>
                  <td>
                    <UserActionsMenu
                      user={u}
                      {...menuHandlers}
                      onView={(user) => navigate(`/admin/users/${user.id}`)}
                      onHistory={(user) => navigate(`/admin/users/${user.id}/transactions`)}
                      onBonus={(user) => navigate(`/admin/users/${user.id}#bonus-control`)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-3 flex flex-wrap items-center gap-3 border-t border-white/5">
          <button disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - pageSize))} className="admin-tab">Önceki</button>
          <span className="text-xs text-slate-400">{total ? skip + 1 : 0}-{Math.min(skip + pageSize, total)} / {total}</span>
          <button disabled={skip + pageSize >= total} onClick={() => setSkip(skip + pageSize)} className="admin-tab">Sonraki</button>
          <div className="ml-auto">
            <PageSizeSelect
              value={pageSize}
              disabled={loading}
              onChange={(size) => {
                // Boyut değişince başa dönülür: 20'lik listenin 8. sayfasında
                // 500'e geçmek toplamın dışında boş bir sayfa gösterirdi.
                setSkip(0);
                setPageSize(size);
                writePageSize('users', size);
              }}
            />
          </div>
        </div>
      </div>

      {error && !loading && <div className="mt-4"><AdminError message={error} onRetry={load} /></div>}

      {dialogs}
      {exportOpen && (
        <ExportDialog
          search={search}
          status={status}
          total={total}
          onClose={() => setExportOpen(false)}
        />
      )}
    </div>
  );
};

export default Users;
